import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import SummaryBox from 'components/page/process/variableRetrieval/SummaryBox';
import CoordinateSystemsList from 'components/page/process/variableRetrieval/CoordinateSystemsList';
import OutputDatastreamsList from 'components/page/process/variableRetrieval/OutputDatastreamsList';
import RetrievedVariablesList from 'components/page/process/variableRetrieval/RetrievedVariablesList';
import RetrievedVariablesTable from 'components/page/process/variableRetrieval/RetrievedVariablesTable';
import EntityHeader from 'components/core/EntityHeader';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import ProcessValidator from 'utils/ProcessValidator';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import NavigationUtils from 'utils/NavigationUtils';
import Constants from 'utils/Constants';
import InfoMessages from 'components/core/help/InfoMessages';
import AbstractForm from 'components/page/process/AbstractForm';
import CollapsiblePanel from 'components/core/CollapsiblePanel';
import OverviewViewDropdown from 'components/core/OverviewViewDropdown';

/**
 * The form that renders the variable retrieval
 */
export default class VariableRetrievalForm extends AbstractForm {

    static MODE_LIST = 'interdependencies';
    static MODE_TABLE = 'table';

    static MENU_ID_OUTPUT_DATASTREAMS = 'outputDatastreamsContextMenu';
    static MENU_ID_COORD_SYS = 'coordinateSystemsContextMenu';
    static MENU_ID_VARIABLES = 'retrievedVariablesContextMenu';

    static propTypes = {
        process: PropTypes.object,
        processId: PropTypes.string,
        revisionNum: PropTypes.string,
        processErrors: PropTypes.object,
        hasWritePermissions: PropTypes.bool,
        dods: PropTypes.array,
        dodsById: PropTypes.object,
        getListEntityForEvent: PropTypes.func,
        getBackgroundColor: PropTypes.func
    };

    constructor(props) {
        super(props);

        // Initialize Props-dependent variables
        this._initializePropDependentVariables(props);
    }

    /**
     * Called when props are passed to the component on an update. NOT when a state change occurs after calling setState().
     * NOT when the component is first mounted.  Often initializion code needs to be duplicated here and in constructor.
     * We can safely call setState() here and it won't re-render.
     * @param nextProps
     */
    componentWillUpdate(nextProps, nextState) {
        this._initializePropDependentVariables(nextProps);
    }

    _initializePropDependentVariables(props) {
        this._sortVariableIds(props);
        this.contextMenuItems = [
            {
                label: 'Delete',
                data: { action: 'delete' },
                disabled: !props.hasWritePermissions
            },
            {
                label: 'Open in new window',
                data: { action: 'open-window' },
                disabled: false
            }
        ];
        this.variableContextMenu = [
            {
                label: 'Edit',
                data: { action: 'edit' },
                disabled: !props.hasWritePermissions
            },
            {
                label: 'Delete',
                data: { action: 'delete' },
                disabled: !props.hasWritePermissions
            },
            {
                label: 'Open in new window',
                data: { action: 'open-window' },
                disabled: false
            }
        ];

        this.validator = new ProcessValidator(props.process);
    }

    _sortVariableIds(props) {
        this.sortedRetrievedVariableIds = [];
        this.sortedInputDatasetIds = [];

        if (props.process) {
            const sortedIds = ProcessUtils.sortRetrievedVariablesByDataset(props.process);
            this.sortedRetrievedVariableIds = sortedIds.sortedRetrievedVariableIds;
            this.sortedInputDatasetIds = sortedIds.sortedInputDatasetIds;
        }
    }

    onDoubleClickRetrievedVariable = (e) => {
        NavigationUtils.gotoRetrievedVariable(this.props.processId, this.props.revisionNum, this.props.getListEntityForEvent(e), false);
    };

    onDoubleClickCoordinateSystem = (e) => {
        NavigationUtils.gotoCoordinateSystem(this.props.processId, this.props.revisionNum, this.props.getListEntityForEvent(e), false);
    };

    onDoubleClickOutputDatastream = (e) => {
        NavigationUtils.gotoOutputDatastream(this.props.processId, this.props.revisionNum, this.props.getListEntityForEvent(e), false);
    };

    selectedEntityChanged(entityType) {
        return (selectedItems, lastSelectedItem) => {
            if (this.summaryBoxComponent && lastSelectedItem) {
                this.summaryBoxComponent.entityChanged(lastSelectedItem, entityType);
            }
        };
    }

    onOutputDatastreamsContextMenuClicked = (e, data) => {
        if (data.action === 'delete') {
            this.onDeleteOutputDatastreams(data.selectedItems);
        } else if (data.action === 'open-window') {
            Object.keys(data.selectedItems).forEach(name => {
                const selected = data.selectedItems[name];
                if (selected) {
                    NavigationUtils.gotoOutputDatastream(this.props.processId, this.props.revisionNum, name, true);
                }
            });
        }
    };

    onCoordinateSystemsContextMenuClicked = (e, data) => {
        if (data.action === 'delete') {
            this.onDeleteCoordinateSystems(data.selectedItems);
        } else if (data.action === 'open-window') {
            Object.keys(data.selectedItems).forEach(name => {
                const selected = data.selectedItems[name];
                if (selected) {
                    NavigationUtils.gotoCoordinateSystem(this.props.processId, this.props.revisionNum, name, true);
                }
            });
        }
    };

    onRetrievedVariablesContextMenuClicked = (e, data) => {
        if (data.action === 'delete') {
            this.onDeleteRetrievedVariables(data.selectedItems);
        } else if (data.action === 'edit') {
            this.onEditRetrievedVariables(data.selectedItems);
        } else if (data.action === 'open-window') {
            Object.keys(data.selectedItems).forEach(name => {
                const selected = data.selectedItems[name];
                if (selected) {
                    NavigationUtils.gotoRetrievedVariable(this.props.processId, this.props.revisionNum, name, true);
                }
            });
        }
    };

    onDeleteOutputDatastreams = (selectedItems) => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'output_datastreams', selectedItems,
            this.validator.canOutputDatastreamsBeDeleted);
    };

    onDeleteCoordinateSystems = (selectedItems) => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'variable_retrieval.coordinate_systems', selectedItems,
            this.validator.canCoordSystemsBeDeleted);
    };

    onDeleteRetrievedVariables = (selectedItems) => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'variable_retrieval.retrieved_variables', selectedItems,
            this.validator.canRetrievedVariablesBeDeleted);
    };

    onEditRetrievedVariables = (selectedItems) => {
        ComponentUtils.openMultiEditVariablesModal(this.props.process, selectedItems);
    };

    getMode = () => {
        // Determine the mode based on the URL
        const segments = NavigationUtils.getPathSegments(this.props.location.pathname);
        let pos = 3;
        if (this.props.revisionNum) {
            pos = 5;
        }
        if (segments.length === pos) {
            return VariableRetrievalForm.MODE_LIST;
        } else {
            return VariableRetrievalForm.MODE_TABLE;
        }
    };

    getShowDataSources() {
        return typeof(this.getLSState().showDataSources) !== 'undefined' ? this.getLSState().showDataSources : true;
    }

    onShowDataSourcesClicked = () => {
        const showDataSources = this.getShowDataSources() ? false : true;
        this.setLSState({ showDataSources });
    };

    renderTableView() {
        return (
            <Flexbox className="variable-retrieval-form " flexDirection="row" flexGrow={1}>
                <Flexbox className="noshrink" flexDirection="row" flexGrow={1}>
                    {/* Retrieved Variables */}
                    <Flexbox className="entity-box" flexDirection="column" flexGrow={1} width="100%">
                        <Flexbox flexDirection="row" flexGrow={1}>
                            <RetrievedVariablesTable
                                process={this.props.process}
                                processId={this.props.processId}
                                revisionNum={this.props.revisionNum}
                                processErrors={this.props.processErrors}
                                dodsById={this.props.dodsById}
                                data={this.props.process.variable_retrieval.retrieved_variables}
                                mode={this.getMode()}
                                hasWritePermissions={this.props.hasWritePermissions}
                                onEditRetrievedVariables={this.onEditRetrievedVariables}
                                onDeleteRetrievedVariables={this.onDeleteRetrievedVariables}
                            />
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    }

    renderListView() {
        // We use a ref function to save an instance to the custom component in our class
        const retrievedVariablesRefFunction = (component) => { this.retrievedVariablesComponent = component; };
        const coordinateSystemsRefFunction = (component) => { this.coordinateSystemsComponent = component; };
        const outputDatastreamsRefFunction = (component) => { this.outputDatastreamsComponent = component; };
        const summaryBoxRefFunction = (component) => { this.summaryBoxComponent = component; };

        let dataSourcesText = 'Show Data Sources';
        let dataSourcesIconClass = 'fa-eye';
        if (this.getShowDataSources()) {
            dataSourcesText = 'Hide Data Sources';
            dataSourcesIconClass = 'fa-eye-slash';
        }

        return (
            <Flexbox className="variable-retrieval-form" flexDirection="row" flexGrow={1}>
                <Flexbox className="scrollable noshrink" flexDirection="row" flexGrow={2}>

                    {/* Retrieved Variables TODO refactor - shouldn't need to pass in validate and onNavClick functions*/}
                    <Flexbox flexDirection="column" padding="0 10px 0 0" className="entity-box retrieved-variables">
                        <RetrievedVariablesList
                            id={`${this.props.process.name}-retrieved-variables`}
                            sortedIds={this.sortedRetrievedVariableIds}
                            data={this.props.process.variable_retrieval.retrieved_variables}
                            process={this.props.process}
                            onDoubleClick={this.onDoubleClickRetrievedVariable}
                            type={Constants.TYPE_RETRIEVED_VARIABLE}
                            onSelectionChanged={this.selectedEntityChanged(Constants.TYPE_RETRIEVED_VARIABLE)}
                            getBackgroundColor={this.props.getBackgroundColor}
                            filterable
                            onContextMenuItemClicked={this.onRetrievedVariablesContextMenuClicked}
                            contextMenuItems={this.variableContextMenu}
                            contextMenuId={VariableRetrievalForm.MENU_ID_VARIABLES}
                            ref={retrievedVariablesRefFunction}
                            processErrors={this.props.processErrors}
                            baseFieldPath={['variable_retrieval', 'retrieved_variables']}
                            showDataSources={this.getShowDataSources()}
                        >
                            <EntityHeader
                                title="Retrieved Variables"
                                count={Object.keys(this.props.process.variable_retrieval.retrieved_variables).length}
                                infoboxMessage={InfoMessages.retrievedVariable}
                            >
                                <Toolbar>
                                    <OverviewViewDropdown
                                        mode={this.getMode()}
                                        processId={this.props.processId}
                                        revisionNum={this.props.revisionNum}
                                    />
                                    <ToolbarItem
                                        title={dataSourcesText}
                                        className={`fa ${dataSourcesIconClass} blue`}
                                        clickHandler={this.onShowDataSourcesClicked}
                                        selectionDependent={false}
                                        enabled
                                        style={{ flexGrow: 1 }}
                                    />
                                    <ToolbarItem
                                        title="Edit Variable Attributes"
                                        className="fa fa-pencil yellow"
                                        clickHandler={this.onEditRetrievedVariables}
                                        selectionDependent
                                        enabled={this.props.hasWritePermissions}
                                    />
                                    <ToolbarItem
                                        title="Delete"
                                        className="fa fa-times delete"
                                        clickHandler={this.onDeleteRetrievedVariables}
                                        selectionDependent
                                        enabled={this.props.hasWritePermissions}
                                    />
                                    <ToolbarItem
                                        title="Add Variables"
                                        className="fa fa-plus new"
                                        clickHandler={ProcessUtils.newRetrievedVariableFunction(this.props.process)}
                                        enabled={this.props.hasWritePermissions}
                                        showText
                                    />
                                </Toolbar>
                            </EntityHeader>
                        </RetrievedVariablesList>
                    </Flexbox>
                </Flexbox>

                <Flexbox flexDirection="column" padding="0 10px" flexGrow={1}>
                    {/* Coordinate Systems */}
                    <Flexbox flexDirection="column" className="entity-box coordinate-systems">
                        <CoordinateSystemsList
                            id={`${this.props.process.name}-coordinate-systems`}
                            sortedIds={Object.keys(this.props.process.variable_retrieval.coordinate_systems)}
                            data={this.props.process.variable_retrieval.coordinate_systems}
                            onDoubleClick={this.onDoubleClickCoordinateSystem}
                            onSelectionChanged={this.selectedEntityChanged(Constants.TYPE_COORDINATE_SYSTEM)}
                            getBackgroundColor={this.props.getBackgroundColor}
                            type={Constants.TYPE_COORDINATE_SYSTEM}
                            hasWritePermissions={this.props.hasWritePermissions}
                            onContextMenuItemClicked={this.onCoordinateSystemsContextMenuClicked}
                            contextMenuItems={this.contextMenuItems}
                            contextMenuId={VariableRetrievalForm.MENU_ID_COORD_SYS}
                            ref={coordinateSystemsRefFunction}
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            baseFieldPath={['variable_retrieval', 'coordinate_systems']}
                        >
                            <EntityHeader
                                title="Coordinate Systems"
                                count={Object.keys(this.props.process.variable_retrieval.coordinate_systems).length}
                                infoboxMessage={InfoMessages.coordinateSystem}
                            >
                                <Toolbar>
                                    <ToolbarItem title="Delete"
                                        className="fa fa-times delete"
                                        clickHandler={this.onDeleteCoordinateSystems}
                                        selectionDependent
                                        enabled={this.props.hasWritePermissions}
                                    />
                                    <ToolbarItem title="New"
                                        className="fa fa-plus new"
                                        clickHandler={ProcessUtils.newCoordinateSystemFunction(this.props.process)}
                                        enabled={this.props.hasWritePermissions}
                                    />
                                </Toolbar>
                            </EntityHeader>
                        </CoordinateSystemsList>
                    </Flexbox>

                    {/* Output Datastreams */}
                    <Flexbox flexDirection="column" className="entity-box output-datastreams">
                        <OutputDatastreamsList
                            id={`${this.props.process.name}-output-datastreams`}
                            sortedIds={this.props.process.output_datastreams}
                            process={this.props.process}
                            onDoubleClick={this.onDoubleClickOutputDatastream}
                            onSelectionChanged={this.selectedEntityChanged(Constants.TYPE_OUTPUT_DATASTREAM)}
                            getBackgroundColor={this.props.getBackgroundColor}
                            type={Constants.TYPE_OUTPUT_DATASTREAM}
                            onContextMenuItemClicked={this.onOutputDatastreamsContextMenuClicked}
                            contextMenuItems={this.contextMenuItems}
                            contextMenuId={VariableRetrievalForm.MENU_ID_OUTPUT_DATASTREAMS}
                            hasWritePermissions={this.props.hasWritePermissions}
                            ref={outputDatastreamsRefFunction}
                            processErrors={this.props.processErrors}
                            baseFieldPath={['output_datastreams']}                            
                        >
                            <EntityHeader
                                title="Output Datastreams"
                                count={this.props.process.output_datastreams.length}
                                infoboxMessage={InfoMessages.outputDatastream}
                            >
                                <Toolbar>
                                    <ToolbarItem title="Delete"
                                        className="fa fa-times delete"
                                        clickHandler={this.onDeleteOutputDatastreams}
                                        selectionDependent
                                        enabled={this.props.hasWritePermissions}
                                    />
                                    <ToolbarItem title="New"
                                        className="fa fa-plus new"
                                        clickHandler={ProcessUtils.newOutputDatastreamFunction(this.props.process)}
                                        enabled={this.props.hasWritePermissions}
                                    />
                                </Toolbar>
                            </EntityHeader>
                        </OutputDatastreamsList>
                    </Flexbox>
                </Flexbox>
                <Flexbox flexDirection="column" padding="0 0 0 2px" className={'right-sidebar'}>
                    <CollapsiblePanel>
                        <SummaryBox ref={summaryBoxRefFunction} process={this.props.process} navId={this.props.processId} revisionNumber={this.props.revisionNum}/>
                    </CollapsiblePanel>
                </Flexbox>
            </Flexbox>
        );
    }

    render = () => {
        const mode = this.getMode();
        if (mode === VariableRetrievalForm.MODE_TABLE) {
            return this.renderTableView();

        } else {
            return this.renderListView();
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/VariableRetrievalForm.js